import { ProfileButton } from "@/features/auth";
import { AppMenu } from "../components/AppMenu";
import { Outlet, useLocation } from "react-router-dom";

import { MobileHeader } from "@/shared/components/MobileHeader";
import { ResponsiveViewWrapper } from "@/shared/components/ResponsiveViewWrapper";
import { TeacherBottomNavBar } from "../navigation/TeacherBottomNavBar";
import { TEACHER_NAVIGATION } from "../navigation/teacher-navigation";

/**
 * Shared mobile layout shell for the Teacher Portal.
 * Wraps the teacher destination screens, including the score-entry introduction.
 * Score entry and meeting joins own standalone layouts.
 *
 * Provides:
 * 1. Branded mobile device frame with signature `#0177BF` border.
 * 2. Mobile top app bar with synchronized Persian subtitle, NO back button (as this is the teacher's root),
 *    and a prominent Logout action matching Flutter's `TeacherPortalHeader`.
 * 3. Elevated, hovering bottom navigation bar matching Flutter's `TeacherBottomNavBar.dart`.
 * 4. Automatic URL route synchronization with browser history and deep linking.
 */
export function TeacherPortalLayout() {
  const location = useLocation();

  const tabSubtitle =
    TEACHER_NAVIGATION.find(
      (tab) =>
        location.pathname === tab.path ||
        location.pathname.startsWith(`${tab.path}/`),
    )?.subtitle ?? TEACHER_NAVIGATION[0].subtitle;

  return (
    <ResponsiveViewWrapper
      maxMobileWidth={430}
      className="bg-frame-surface"
      scrollable={false}
      aria-label="پرتال آموزگار"
    >
      {/* Mobile Top App Bar: No back button on root dashboard, includes logout action */}
      <MobileHeader
        leadingAction={<ProfileButton />}
        title="کایرسافت"
        subtitle={tabSubtitle}
        showBack={false}
        trailingAction={<AppMenu />}
      />

      {/* Main Body with scrollable content and hovering navbar.
          This element is `relative` on purpose: it is the positioning context that keeps
          `absolute inset-0` overlays inside the phone frame instead of letting them resolve
          against the browser viewport. Removing `relative` sends any such overlay to the
          whole desktop viewport. */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Scrollable Container with bottom padding so content never collides with floating bar */}
        <div
          className="min-h-0 flex-1 space-y-3.5 overflow-x-hidden overflow-y-auto p-4 pb-28 select-none"
          dir="rtl"
        >
          <Outlet />
        </div>

        {/* Hovering Bottom Navigation Bar */}
        <TeacherBottomNavBar />
      </div>
    </ResponsiveViewWrapper>
  );
}
